import React, { useCallback, useEffect, useMemo, useState } from "react";
import { RefreshCw, ChevronLeft, ChevronRight, X, UserRound } from "lucide-react";
import moment from "moment";
import toast from "react-hot-toast";

import {
  deleteDevelopmentSearch,
  getDevelopmentSavedSearches,
  getNewDevelopments,
  saveDevelopmentSearch,
} from "../../../api/dashboard";
import { DevelopmentRow } from "../../dashboard/components/MarketSignalAlertsPanel";
import SuburbChipSearch from "../components/SuburbChipSearch";
import DevelopmentFilterSidebar, { EMPTY_FILTERS } from "../components/DevelopmentFilterSidebar";
import DevelopmentDetailPanel from "../components/DevelopmentDetailPanel";

/**
 * New Developments (Christian Gravias, Monday 12899126094, 23 Oct 2026):
 * "Please create a New Developments tab to sit under Re-listing opportunities
 * ... similar to how we did it with re-listing opportunities where agents can
 * search suburbs and change the stages of the projects so they can look for
 * particular projects."
 *
 * Round 3 (same day): "add a sidebar like BCI" (DevelopmentFilterSidebar, which
 * stays open), "make every project clickable to show all the info like BCI
 * does" (DevelopmentDetailPanel) and "Add Abandoned as its own category" (the
 * last stage tab).
 *
 * 24 Oct 2026: "add the owner details button next to Abandoned like we have
 * done for re-listing opportunities". It is the side menu's "Developer listed
 * only" filter (a developer, owner, client or proponent BCI names), as a pill.
 *
 * Every open BCI (Hubexo) project in the patch, from the weekly sync. "View
 * All" on the dashboard tile lands here; a tile row lands here with
 * ?project=<id> and opens that project.
 */

const PAGE_SIZE = 50;

const SORT_OPTIONS = [
  { key: "updated", label: "Recently updated" },
  { key: "start", label: "Construction start, soonest" },
  { key: "value_high", label: "Value high to low" },
  { key: "value_low", label: "Value low to high" },
  { key: "units", label: "Most units" },
  { key: "suburb", label: "Suburb A to Z" },
];

// Sidebar list filters -> the API's comma-list params.
const LIST_PARAMS = {
  statuses: "status",
  categories: "category",
  development_types: "development_type",
  ownership_types: "ownership_type",
  roles: "role",
};
const SCALAR_PARAMS = [
  "value_min",
  "value_max",
  "updated_days",
  "units_min",
  "units_max",
  "storeys_min",
  "storeys_max",
];

export const filterParams = (f) => {
  const p = {};
  Object.entries(LIST_PARAMS).forEach(([key, param]) => {
    if (f[key].length) p[param] = f[key].join(",");
  });
  SCALAR_PARAMS.forEach((k) => {
    if (f[k]) p[k] = f[k];
  });
  // Month pickers: from the 1st of the first month to the end of the last.
  if (f.start_from) p.start_from = `${f.start_from}-01`;
  if (f.start_to) p.start_to = moment(f.start_to, "YYYY-MM").endOf("month").format("YYYY-MM-DD");
  if (f.has_developer) p.has_developer = 1;
  return p;
};

// A saved search's params back into page state.
const fromSaved = (params = {}) => {
  const split = (v) => (v ? String(v).split(",").filter(Boolean) : []);
  const f = { ...EMPTY_FILTERS };
  Object.entries(LIST_PARAMS).forEach(([key, param]) => {
    f[key] = split(params[param]);
  });
  SCALAR_PARAMS.forEach((k) => {
    f[k] = params[k] ? String(params[k]) : "";
  });
  f.start_from = params.start_from ? String(params.start_from).slice(0, 7) : "";
  f.start_to = params.start_to ? String(params.start_to).slice(0, 7) : "";
  f.has_developer = ["1", "true", "True"].includes(String(params.has_developer || ""));
  return {
    filters: f,
    query: params.q || "",
    suburbs: split(params.suburb),
    stages: split(params.stage),
    sort: params.sort || "updated",
  };
};

const projectFromUrl = () => {
  try {
    const id = new URLSearchParams(window.location.search).get("project");
    return id ? { id, name: "Loading project" } : null;
  } catch (e) {
    return null;
  }
};

const isWide = () =>
  typeof window === "undefined" || !window.matchMedia || window.matchMedia("(min-width: 1024px)").matches;

const NewDevelopmentsPage = () => {
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ count: 0, totals: {}, suburb_options: [], filter_options: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");
  const [query, setQuery] = useState("");
  const [stages, setStages] = useState([]);
  const [sort, setSort] = useState("updated");
  const [suburbs, setSuburbs] = useState([]);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(EMPTY_FILTERS);

  // Open by default on a wide screen and it stays open (Christian: "Leave this
  // opened"); on a phone it is a sheet opened from the filter button.
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [saved, setSaved] = useState([]);
  const [openRow, setOpenRow] = useState(projectFromUrl);

  const params = useMemo(() => {
    const p = { ...filterParams(filters), sort, page, page_size: PAGE_SIZE };
    if (query) p.q = query;
    if (stages.length) p.stage = stages.join(",");
    if (suburbs.length) p.suburb = suburbs.join(",");
    return p;
  }, [filters, sort, page, query, stages, suburbs]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await getNewDevelopments(params);
      setRows(res.data.results || []);
      setMeta(res.data);
    } catch (err) {
      console.error("Error loading new developments:", err);
      setError("Could not load new developments.");
    } finally {
      setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    load();
  }, [load]);

  // Any filter change goes back to page 1.
  useEffect(() => {
    setPage(1);
  }, [filters, sort, query, stages, suburbs]);

  useEffect(() => {
    getDevelopmentSavedSearches()
      .then((res) => setSaved(res?.data?.results || []))
      .catch(() => setSaved([]));
  }, []);

  const setFilter = (key, value) => setFilters((f) => ({ ...f, [key]: value }));
  const toggleIn = (key, value) =>
    setFilters((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((v) => v !== value) : [...f[key], value],
    }));

  const sidebarCount = Object.keys(filterParams(filters)).length;
  const pages = Math.max(1, Math.ceil((meta.count || 0) / PAGE_SIZE));
  const totals = meta.totals || {};
  const stageOptions = totals.stages || [];
  const stageTabs = [{ stage: "", label: "All", count: totals.all }].concat(
    stageOptions.map((s) => ({ stage: s.stage, label: s.stage, count: s.count })),
  );
  // A picked stage with no projects for the new search keeps its tab, so it can be switched off.
  stages.forEach((s) => {
    if (!stageTabs.some((t) => t.stage === s)) stageTabs.push({ stage: s, label: s, count: 0 });
  });
  const tabSelected = (t) => (t.stage ? stages.length === 1 && stages[0] === t.stage : stages.length === 0);

  const clearAll = () => {
    setSearchTerm("");
    setQuery("");
    setStages([]);
    setSuburbs([]);
    setFilters(EMPTY_FILTERS);
  };
  const anyFilter = query || stages.length || suburbs.length || sidebarCount > 0;

  const savedParams = () => {
    const { page: _p, page_size: _s, ...rest } = params;
    return rest;
  };
  const saveCurrent = async (name) => {
    try {
      const res = await saveDevelopmentSearch(name, savedParams());
      setSaved((list) =>
        [...list.filter((s) => s.id !== res.data.id), res.data].sort((a, b) => a.name.localeCompare(b.name)),
      );
      toast.success(`Saved "${res.data.name}"`);
      return true;
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save this search.");
      return false;
    }
  };
  const applySaved = (s) => {
    const next = fromSaved(s.params);
    setFilters(next.filters);
    setQuery(next.query);
    setSearchTerm(next.query);
    setSuburbs(next.suburbs);
    setStages(next.stages);
    setSort(next.sort);
    setSheetOpen(false);
  };
  const deleteSaved = async (s) => {
    if (!window.confirm(`Delete the saved search "${s.name}"?`)) return;
    try {
      await deleteDevelopmentSearch(s.id);
      setSaved((list) => list.filter((x) => x.id !== s.id));
    } catch (err) {
      toast.error("Could not delete that saved search.");
    }
  };

  const closeDetail = useCallback(() => {
    setOpenRow(null);
    // Drop ?project= so a refresh does not reopen it.
    try {
      const url = new URL(window.location.href);
      if (url.searchParams.has("project")) {
        url.searchParams.delete("project");
        window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
      }
    } catch (e) {
      /* old browser: leave the URL alone */
    }
  }, []);

  const sidebar = (mobile) => (
    <DevelopmentFilterSidebar
      mobile={mobile}
      filters={filters}
      setFilter={setFilter}
      toggleIn={toggleIn}
      query={query}
      onQuery={(q) => {
        setQuery(q);
        setSearchTerm(q);
      }}
      suburbs={suburbs}
      onSuburbs={setSuburbs}
      suburbOptions={meta.suburb_options || []}
      stages={stages}
      onStages={setStages}
      stageOptions={stageOptions}
      options={meta.filter_options || {}}
      savedSearches={saved}
      onApplySaved={applySaved}
      onDeleteSaved={deleteSaved}
      onSaveCurrent={saveCurrent}
      canSave={Boolean(anyFilter) || sort !== "updated"}
      onClearAll={clearAll}
      onClose={() => (mobile ? setSheetOpen(false) : setSidebarOpen(false))}
      resultCount={meta.count || 0}
    />
  );

  return (
    <div className="w-full">
      <div className="px-4 sm:px-6 pt-5">
        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-2">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">New Developments</h1>
            <p className="text-[13px] text-gray-500 mt-0.5">
              Upcoming construction projects in our suburbs from BCI, with the companies behind each one where BCI
              lists them. Click a project to see everything BCI has on it.
            </p>
          </div>
          <div className="flex items-center gap-2 text-[12px] text-gray-400">
            {meta.last_synced_at && <span>BCI data updated {moment(meta.last_synced_at).fromNow()}</span>}
            <button onClick={load} className="p-1.5 hover:text-teal-600" title="Refresh">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            </button>
          </div>
        </div>
      </div>

      <div className="px-4 sm:px-6 py-4 w-full">
        <div className="flex gap-5 items-start">
          {sidebarOpen && (
            <aside
              className="hidden lg:flex flex-col w-[264px] flex-shrink-0 sticky top-4 max-h-[calc(100vh-2rem)]"
              aria-label="Side menu"
            >
              {sidebar(false)}
            </aside>
          )}

          <div className="flex-1 min-w-0">
            <div className="mb-6">
              <SuburbChipSearch
                options={meta.suburb_options || []}
                selected={suburbs}
                onChange={setSuburbs}
                text={searchTerm}
                onTextChange={setSearchTerm}
                onSearch={setQuery}
                onFilterClick={() => (isWide() ? setSidebarOpen(!sidebarOpen) : setSheetOpen(true))}
                showFilters={sidebarOpen && isWide()}
                activeFilterCount={sidebarCount}
                placeholder="Search suburb, postcode, project name or developer"
              />
            </div>

            {/* Stage tabs: one line, scrolling sideways on a narrow screen. Owner details follows
                Abandoned, or wraps just under the tabs when the side menu leaves no room. */}
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <div className="max-w-full overflow-x-auto [scrollbar-width:none]">
                <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1 w-max" role="tablist">
                  {stageTabs.map((t) => (
                    <button
                      key={t.stage || "all"}
                      role="tab"
                      aria-selected={tabSelected(t)}
                      onClick={() => setStages(t.stage ? [t.stage] : [])}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-semibold whitespace-nowrap transition-all ${
                        tabSelected(t) ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {t.label}
                      {typeof t.count === "number" && (
                        <span className="ml-1 text-[11px] text-gray-400 tabular-nums">{t.count}</span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setFilter("has_developer", !filters.has_developer)}
                aria-pressed={filters.has_developer}
                title="Only projects where BCI names the developer or owner, with their phone and email where BCI has them"
                className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-[12px] font-semibold border whitespace-nowrap transition-all ${
                  filters.has_developer
                    ? "bg-[#55C5D0] border-[#55C5D0] text-white"
                    : "bg-white border-gray-200 text-gray-500 hover:text-gray-700"
                }`}
              >
                <UserRound className="w-3.5 h-3.5" />
                Owner details
              </button>
            </div>

            <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
              <div className="text-sm text-gray-600">
                Showing <span className="font-bold text-[#55C5D0] tabular-nums">{meta.count || 0}</span> projects
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <select
                  value={sort}
                  onChange={(e) => setSort(e.target.value)}
                  className="bg-white border border-gray-200 rounded-lg px-2.5 py-1.5 text-[12px] text-gray-700"
                  aria-label="Sort"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.key} value={o.key}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {anyFilter && (
                  <button
                    onClick={clearAll}
                    className="inline-flex items-center gap-1 text-[12px] font-semibold text-gray-500 hover:text-red-500"
                  >
                    <X className="w-3.5 h-3.5" /> Clear
                  </button>
                )}
              </div>
            </div>

            <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4">
              {error ? (
                <div className="py-10 text-center text-sm text-red-500">{error}</div>
              ) : loading && rows.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
              ) : rows.length === 0 ? (
                <div className="py-10 text-center text-sm text-gray-500">
                  {meta.last_synced_at
                    ? "No projects match these filters."
                    : "Waiting for the first BCI sync. The list fills in after it runs."}
                </div>
              ) : (
                rows.map((row) => <DevelopmentRow key={row.id} row={row} onOpen={setOpenRow} />)
              )}
            </div>

            {pages > 1 && (
              <div className="flex items-center justify-center gap-3 mt-4 text-[13px] text-gray-600">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page <= 1}
                  className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                Page {page} of {pages}
                <button
                  onClick={() => setPage((p) => Math.min(pages, p + 1))}
                  disabled={page >= pages}
                  className="p-1.5 rounded-lg border border-gray-200 disabled:opacity-40"
                  aria-label="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {sheetOpen && (
        <div
          className="lg:hidden fixed inset-0 z-[60] bg-white p-3 flex flex-col"
          role="dialog"
          aria-modal="true"
          aria-label="Filters"
        >
          {sidebar(true)}
        </div>
      )}

      {openRow && <DevelopmentDetailPanel key={openRow.id} row={openRow} onClose={closeDetail} />}
    </div>
  );
};

export default NewDevelopmentsPage;
